import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(false);
  const [themeLoaded, setThemeLoaded] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    let initialTheme = false;
    if (savedTheme === 'dark') initialTheme = true;
    else if (savedTheme === 'light') initialTheme = false;
    else initialTheme = prefersDark;
    setIsDark(initialTheme);
    setThemeLoaded(true);
  }, []);

  useEffect(() => {
    if (!themeLoaded) return; // wait until initial theme resolved
    if (typeof window === 'undefined') return; // SSR guard
    const root = document.documentElement; // <html>
    const body = document.body;
    // Toggle dark mode class (Tailwind dark mode expects this)
    root.classList.toggle('dark', isDark);
    // Ensure we don't keep both bg-white and bg-black at once
    body.classList.remove(isDark ? 'bg-white' : 'bg-black');
    body.classList.add(isDark ? 'bg-black' : 'bg-white');
  }, [isDark, themeLoaded]);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    try {
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    } catch (e) {
      // Ignore write errors (e.g., private mode)
    }
  };

  if (!themeLoaded) {
    return <div className="min-h-screen bg-white dark:bg-black" />;
  }

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
